<template>
  <div class="agent-plaza">
    <div class="agents-grid" v-if="filteredAgents.length > 0">
      <div v-for="agent in filteredAgents" :key="agent.agentId" class="agent-card" :class="{ selected: selectedAgentId === agent.agentId }" @click.stop="goToAgentProfile(agent)">
        <div class="selected-check" v-if="selectedAgentId === agent.agentId">✓</div>
        <div class="agent-avatar" :style="{ background: agent.avatarUrl ? 'transparent' : agent.color }">
          <img v-if="agent.avatarUrl" :src="agent.avatarUrl" />
          <span v-else>{{ agent.icon }}</span>
        </div>
        <div class="agent-name">{{ agent.name }}</div>
        <div class="agent-stats">
          <span class="subscribers">{{ formatSubscribers(agent.subscribers) }}人</span>
          <span class="price">{{ agent.price > 0 ? '¥' + agent.price : '免费' }}</span>
          <span class="access-scope">{{ formatAccessScope(agent.accessScope) }}</span>
        </div>
        <div class="agent-tooltip">
          <div class="tooltip-desc">{{ agent.description || '暂无描述' }}</div>
          <button class="use-btn" @click.stop="selectAgent(agent)">立即使用</button>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loading-state">加载中...</div>
    <p v-if="!loading && filteredAgents.length === 0 && searchText" style="text-align:center;color:#999;font-size:13px;margin:12px 0;">未找到匹配的智能体</p>
  </div>
</template>

<script>
import creatorApi from '@/api/creatorApi'

const COLORS = ['#5B8FF9', '#61DDAA', '#F6BD16', '#E86452', '#6DC8EC', '#9270CA', '#FF6B9D', '#10A37F']
const ICONS = ['🤖', '💼', '📊', '✍️', '📋', '🌐', '💻', '🚀']

export default {
  name: 'AgentPlazaContent',
  props: {
    searchText: { type: String, default: '' },
    selectedAgentId: { type: String, default: '' }
  },
  data() {
    return {
      agents: [],
      loading: false
    }
  },
  computed: {
    filteredAgents() {
      if (!this.searchText) return this.agents
      const kw = this.searchText.toLowerCase()
      return this.agents.filter(a => 
        (a.name || '').toLowerCase().includes(kw) || 
        (a.description || '').toLowerCase().includes(kw)
      )
    }
  },
  mounted() {
    this.loadAgents()
  },
  methods: {
    async loadAgents() {
      this.loading = true
      try {
        const isLoggedIn = this.$store.getters.isLoggedIn
        const data = await creatorApi.getAgentPlaza(50, !isLoggedIn)
        this.agents = (data || []).map((agent, i) => ({
          ...agent,
          icon: agent.icon || ICONS[i % ICONS.length],
          color: agent.color || COLORS[i % COLORS.length]
        }))
      } catch (e) {
        console.error('加载智能体失败:', e)
        this.agents = []
      } finally {
        this.loading = false
      }
    },
    selectAgent(agent) {
      const isLoggedIn = this.$store.getters.isLoggedIn
      if (!isLoggedIn) {
        this.goToAgentProfile(agent)
      } else {
        this.$emit('select-agent', agent)
      }
    },
    goToAgentProfile(agent) {
      if (agent.agentId) {
        this.$router.push(`/agent/profile/a/${agent.agentId}`);
      }
    },
    formatSubscribers(count) {
      if (!count) return 0
      if (count >= 10000) return (count / 10000).toFixed(1) + '万'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'k'
      return count
    },
    formatAccessScope(scope) {
      const map = { PUBLIC: '公开', FRIENDS: '好友', GROUP_MEMBERS: '群成员' }
      return map[scope] || '公开'
    }
  }
}
</script>

<style lang="scss" scoped>
.agent-plaza { padding: 8px 0; min-height: 120px; }
.agents-grid {
  display: flex; flex-wrap: wrap; gap: 12px;
  padding: 8px; background: #f8f9fa; border-radius: 8px;
}
.agent-card {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 8px 10px; background: white; border-radius: 6px; border: 1px solid #e5e5e5;
  transition: all 0.2s; cursor: pointer; position: relative;
  &:hover { transform: translateY(-2px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 1001; }
  &:hover .agent-tooltip, .agent-tooltip:hover { display: flex; }
  &.selected {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102,126,234,0.2);
  }
}
.selected-check {
  position: absolute; top: -8px; right: -8px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #667eea; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: bold; z-index: 10;
}
.agent-avatar {
  width: 36px !important; height: 36px !important; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.agent-name { font-size: 11px; color: #666; white-space: nowrap; }
.agent-stats {
  display: flex; gap: 6px; font-size: 10px; color: #999;
  .subscribers { color: #666; }
  .price { color: #ff6b6b; font-weight: 500; }
  .access-scope { color: #52c41a; }
}
.agent-tooltip {
  display: none; flex-direction: column; gap: 8px;
  position: absolute; bottom: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  background: white; padding: 12px; border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  min-width: 180px; max-width: 280px; z-index: 1000;
  border: 1px solid #e5e5e5;
  &::before {
    content: ''; position: absolute; top: 100%; left: 0; right: 0;
    height: 12px;
  }
  &::after {
    content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    border: 6px solid transparent; border-top-color: white;
  }
  .tooltip-desc {
    font-size: 12px; color: #666; line-height: 1.5; text-align: left;
  }
  .use-btn {
    background: none; border: none; padding: 0;
    color: #667eea; font-size: 12px; cursor: pointer;
    text-align: right;
    &:hover { color: #764ba2; text-decoration: underline; }
  }
}
.loading-state { padding: 40px; text-align: center; color: #999; }
.empty-state { padding: 12px; text-align: center; color: #999; font-size: 13px; background: transparent; max-height: 50px; overflow: hidden; pointer-events: none; }
</style>
